import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, ShieldCheck } from 'lucide-react';
import {
  hasDecided,
  acceptAll,
  rejectAll,
  setConsent,
  getConsent,
  applyPostHogConsent,
  onConsentChange,
} from '../lib/consent';

/**
 * CookieBanner — UK GDPR / PECR-compliant consent prompt.
 *
 * - Slides in on first visit only.  Stays dismissed once the user picks.
 * - Three equally-prominent paths: Accept all · Reject all · Manage.
 * - Manage expands an in-place panel listing the cookie categories.
 *   Essential is always on (greyed switch); analytics defaults off.
 * - Whatever the user picks gets persisted and PostHog is gated
 *   immediately (opt_in / opt_out + reset).
 *
 * Also listens for the global "guardian:openCookieSettings" event so the
 * footer / privacy page links can re-open the panel anytime.
 */
export default function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [managing, setManaging] = useState(false);
  const [prefs, setPrefs] = useState(() => getConsent().categories);

  // Initial mount: open if visitor hasn't decided yet.
  useEffect(() => {
    if (!hasDecided()) {
      // Tiny delay so the banner doesn't compete with hero entrance animations.
      const t = setTimeout(() => setOpen(true), 900);
      return () => clearTimeout(t);
    }
    // Make sure PostHog is gated correctly on every load.
    applyPostHogConsent(getConsent().categories);
    return undefined;
  }, []);

  // Allow other UI (Footer link, Privacy page) to re-open the panel.
  useEffect(() => {
    const onOpen = () => {
      setPrefs(getConsent().categories);
      setManaging(true);
      setOpen(true);
    };
    window.addEventListener('guardian:openCookieSettings', onOpen);
    return () => window.removeEventListener('guardian:openCookieSettings', onOpen);
  }, []);

  // Apply consent to PostHog any time the stored choice changes.
  useEffect(() => {
    const unsub = onConsentChange((payload) => {
      applyPostHogConsent(payload ? payload.categories : getConsent().categories);
    });
    return unsub;
  }, []);

  const handleAccept = () => {
    acceptAll();
    setOpen(false);
    setManaging(false);
  };
  const handleReject = () => {
    rejectAll();
    setOpen(false);
    setManaging(false);
  };
  const handleSave = () => {
    setConsent({ analytics: !!prefs.analytics });
    setOpen(false);
    setManaging(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="cookie-banner"
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
          className="fixed z-[60] bottom-4 left-4 right-4 md:bottom-6 md:right-6 md:left-auto md:max-w-[440px]"
          data-testid="cookie-banner"
          role="dialog"
          aria-modal="false"
          aria-labelledby="cookie-banner-title"
        >
          <div
            className="relative rounded-2xl overflow-hidden text-[#E7E9E4]"
            style={{
              backdropFilter: 'blur(20px) saturate(140%)',
              WebkitBackdropFilter: 'blur(20px) saturate(140%)',
              background: 'linear-gradient(150deg, rgba(22,42,30,0.92), rgba(49,79,43,0.88))',
              border: '1px solid rgba(231,233,228,0.18)',
              boxShadow: '0 30px 80px -20px rgba(0,0,0,0.55), inset 0 1px 0 rgba(231,233,228,0.18)',
            }}
          >
            {/* close (×) — equivalent to reject-all per PECR */}
            <button
              type="button"
              onClick={handleReject}
              aria-label="Reject non-essential cookies and close"
              data-testid="cookie-banner-close"
              className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-[#E7E9E4]/10 flex items-center justify-center text-[#E7E9E4]/70 hover:text-[#A3C66F] transition-colors"
            >
              <X size={16} strokeWidth={2} />
            </button>

            <div className="p-5 md:p-6">
              <div className="flex items-center gap-2.5 mb-3">
                <Cookie size={16} className="text-[#A3C66F]" strokeWidth={2.2} />
                <h2
                  id="cookie-banner-title"
                  className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#A3C66F]"
                >
                  Your cookie choices
                </h2>
              </div>

              <p className="text-[13.5px] md:text-sm leading-relaxed text-[#E7E9E4]/90 mb-4">
                We use essential cookies to make our site work. With your permission we'd also
                use analytics cookies to understand how visitors use the site so we
                can make it better. Read our{' '}
                <Link
                  to="/privacy"
                  data-testid="cookie-banner-policy-link"
                  className="underline decoration-[#A3C66F]/60 hover:decoration-[#A3C66F] font-semibold"
                >
                  cookie & privacy policy
                </Link>
                .
              </p>

              {managing && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  className="overflow-hidden"
                >
                  <div className="space-y-2 mb-4 pt-1">
                    <CookieRow
                      title="Essential"
                      desc="Required for the site to work — page navigation, form submissions, secure preferences."
                      checked
                      disabled
                    />
                    <CookieRow
                      title="Analytics"
                      desc="Google Analytics — page views and how the site is used. Off by default. Never used for ads."
                      checked={!!prefs.analytics}
                      onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                      testid="cookie-toggle-analytics"
                    />
                  </div>
                </motion.div>
              )}

              {/* CTAs — Reject + Accept are equally prominent, per UK rules */}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleReject}
                  data-testid="cookie-banner-reject"
                  className="flex-1 min-w-[120px] px-4 py-2.5 rounded-full border border-[#E7E9E4]/30 hover:border-[#A3C66F] text-[#E7E9E4] hover:text-[#A3C66F] text-[11px] tracking-[0.22em] uppercase font-bold transition-colors"
                >
                  Reject all
                </button>
                {!managing ? (
                  <button
                    type="button"
                    onClick={() => setManaging(true)}
                    data-testid="cookie-banner-manage"
                    className="flex-1 min-w-[120px] px-4 py-2.5 rounded-full border border-[#E7E9E4]/30 hover:border-[#A3C66F] text-[#E7E9E4] hover:text-[#A3C66F] text-[11px] tracking-[0.22em] uppercase font-bold transition-colors"
                  >
                    Manage
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSave}
                    data-testid="cookie-banner-save"
                    className="flex-1 min-w-[120px] px-4 py-2.5 rounded-full border border-[#E7E9E4]/30 hover:border-[#A3C66F] text-[#E7E9E4] hover:text-[#A3C66F] text-[11px] tracking-[0.22em] uppercase font-bold transition-colors"
                  >
                    Save choices
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleAccept}
                  data-testid="cookie-banner-accept"
                  className="flex-1 min-w-[120px] inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#A3C66F] hover:bg-[#B7D680] text-[#162A1E] text-[11px] tracking-[0.22em] uppercase font-extrabold transition-colors"
                >
                  <ShieldCheck size={13} strokeWidth={2.6} />
                  Accept all
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function CookieRow({ title, desc, checked, disabled, onChange, testid }) {
  return (
    <label
      className={`flex items-start gap-3 p-3 rounded-xl border border-[#E7E9E4]/12 bg-[#0F1A14]/35 ${
        disabled ? 'opacity-90' : 'cursor-pointer hover:border-[#A3C66F]/40'
      }`}
    >
      <div className="flex-1">
        <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#E7E9E4]">{title}</div>
        <div className="text-[11.5px] text-[#E7E9E4]/75 leading-snug mt-1">{desc}</div>
      </div>
      <div className="mt-0.5 shrink-0">
        <input
          type="checkbox"
          className="sr-only"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange && onChange(e.target.checked)}
          data-testid={testid}
        />
        <span
          aria-hidden="true"
          className={`block w-9 h-5 rounded-full relative transition-colors ${
            checked ? 'bg-[#A3C66F]' : 'bg-[#E7E9E4]/20'
          }`}
        >
          <span
            className={`absolute top-0.5 w-4 h-4 rounded-full bg-[#162A1E] transition-transform ${
              checked ? 'translate-x-[18px]' : 'translate-x-0.5'
            }`}
          />
        </span>
      </div>
    </label>
  );
}

/**
 * Footer / Privacy-page helper — fire to re-open the banner.
 */
export function openCookieSettings() {
  window.dispatchEvent(new CustomEvent('guardian:openCookieSettings'));
}
